/* eslint-disable react/no-array-index-key */
/* eslint-disable no-console */
import React, { useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { IconExportExcel } from '@assets/icons';
import { CHART_COLORS, downloadChartAsImage } from '@common/constant/chart';
import { YEAR_MONTH_FORMAT } from '@common/constant/date';
import { getDateRangeSuffix } from '@common/utils/dateHelper';
import ComLoading from '@components/common/ComLoading';
import { fabAccessLogApi } from '@services/fabAccessLogApi';
import '@styles/pages/AnalyticDetail.scss';
import { Tooltip } from 'antd';
import { saveAs } from 'file-saver';
import { t } from 'i18next';
import { GetMonthStatus } from '@common/utils/optionFilterHelper';
import { isMonthPickerMode } from '@common/utils/modeHelper';

function HorizontalChartModal({
  isVisible,
  onClose,
  chartData,
  isDurationChart = false,
  isLoading = false,
  pagination,
  pickerMode,
  monthOptions = [],
}) {
  const chartRef = useRef(null);

  const GetFromDateWithStatus = useMemo(() => {
    if (monthOptions.length > 0 && pagination.from_date) {
      const fromStatus = GetMonthStatus(monthOptions, pagination.from_date);
      return `${dayjs(pagination.from_date).format(
        YEAR_MONTH_FORMAT,
      )}（${fromStatus}）`;
    }
    return '----/--（--）';
  }, [monthOptions, pagination.from_date]);

  const GetToDateWithStatus = useMemo(() => {
    if (monthOptions.length > 0 && pagination.to_date) {
      const toStatus = GetMonthStatus(monthOptions, pagination.to_date);
      return `${dayjs(pagination.to_date).format(
        YEAR_MONTH_FORMAT,
      )}（${toStatus}）`;
    }
    return '----/--（--）';
  }, [monthOptions, pagination.to_date]);

  if (!isVisible) return null;

  const maxValue = chartData.length > 0 ? chartData[0].value : 1;

  const exportExcelFile = async (nameFile) => {
    try {
      const fileData = await fabAccessLogApi.exportSummaryAccessLogs(
        pagination,
      );
      const blob = new Blob([fileData], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      saveAs(blob, `${nameFile}.xlsx`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    }
  };

  const exportCSVAndDownloadChart = async () => {
    const suffix = getDateRangeSuffix(
      pagination.from_date,
      pagination.to_date,
      pickerMode,
      'YYYYMM',
    );
    const nameFile = isDurationChart
      ? `FaView_分析_総プレイ時間_${suffix}`
      : `FaView_分析_アクセス数_${suffix}`;
    await exportExcelFile(nameFile);
    await downloadChartAsImage(chartRef, `${nameFile}.png`);
  };

  return (
    <div className="chart">
      <div className="chart-modal-overlay" onClick={onClose}>
        <div
          ref={chartRef}
          className="chart-modal-content"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="chart-header">
            <h3>
              {isMonthPickerMode(pickerMode)
                ? `${GetFromDateWithStatus}`
                : `${GetFromDateWithStatus} ~ ${GetToDateWithStatus}`}
            </h3>
            <div className="chart-header-actions">
              <IconExportExcel
                className="pointer"
                onClick={exportCSVAndDownloadChart}
              />
              <div className="close-btn" onClick={onClose}>
                ×
              </div>
            </div>
          </div>
          <div className="chart-body">
            {isLoading ? (
              <ComLoading type="loading" />
            ) : (
              <div className="chart-container">
                {chartData.length > 0 && (
                  <>
                    <div className="chart-content">
                      <div className="chart-bars">
                        <div className="chart-bars-wrapper">
                          <div className="chart-reference-lines">
                            {[0, 25, 50, 75, 100].map((percent) => (
                              <div
                                key={`reference-line-${percent}`}
                                className="chart-reference-line"
                                style={{
                                  left: `${percent}%`,
                                }}
                              />
                            ))}
                          </div>
                          {chartData.map((item, index) => {
                            const percentage = Math.round(
                              (item.value / maxValue) * 100,
                            );
                            const barColor = (() => {
                              const totalItems = chartData.length;

                              const colorIndex = Math.min(
                                Math.floor(
                                  (index / totalItems) * CHART_COLORS.length,
                                ),
                                CHART_COLORS.length - 1,
                              );

                              return CHART_COLORS[colorIndex];
                            })();

                            return (
                              <div
                                key={`chart-${item.name}-${index}`}
                                className="chart-item-horizontal"
                              >
                                <Tooltip
                                  title={
                                    <div>
                                      <div>{item.name}</div>
                                      <div>{item.company}</div>
                                    </div>
                                  }
                                  placement="right"
                                  mouseEnterDelay={0.3}
                                >
                                  <div className="chart-label-left">
                                    <div className="layout-name">
                                      <span
                                        className="color-indicator"
                                        style={{ backgroundColor: barColor }}
                                      />
                                      {item.name}
                                    </div>
                                    <div className="company-name">
                                      ({item.company})
                                    </div>
                                  </div>
                                </Tooltip>
                                <div className="chart-bar-horizontal-container">
                                  <div
                                    className="chart-bar-horizontal"
                                    style={{
                                      width: `${percentage}%`,
                                      backgroundColor: barColor,
                                    }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    <div className="chart-table">
                      <div className="table-header">
                        <div className="header-item">
                          {t('analytic.detail.table.layoutName')}
                        </div>
                        <div className="header-item">
                          {isDurationChart
                            ? t('analytic.detail.table.totalDuration')
                            : t('analytic.detail.table.accessCount')}
                        </div>
                      </div>
                      {chartData.map((item, index) => (
                        <div
                          key={`table-${item.name}-${index}`}
                          className="table-row"
                        >
                          <div className="table-cell">
                            <div>{item.name}</div>
                            <div>({item.company})</div>
                          </div>
                          <div className="table-cell">
                            {isDurationChart
                              ? item.displayValue || item.value
                              : item.value}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

HorizontalChartModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  chartData: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      company: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
    }),
  ).isRequired,
  isDurationChart: PropTypes.bool,
  isLoading: PropTypes.bool,
  pagination: PropTypes.object,
  pickerMode: PropTypes.string,
  monthOptions: PropTypes.any,
};

export default HorizontalChartModal;
